import { useEffect, useState } from "react";
import api from "../../lib/api";
import DashboardShell, { StatCard } from "./DashboardShell";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Trash2, BookmarkCheck, Star, Flag, ShieldOff, ShieldCheck, Download } from "lucide-react";
import { toast } from "sonner";
import { PERIOD_OPTIONS, exportQuery, periodRange } from "../../lib/periods";
import RevenueChart from "../../components/RevenueChart";

export default function AdminDashboard() {
  const [stats, setStats] = useState({});
  const [exportPeriod, setExportPeriod] = useState("all");
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reports, setReports] = useState([]);
  const [tab, setTab] = useState("overview");

  const load = async () => {
    try {
      const [s, u, e, b, r, rp] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/events"),
        api.get("/admin/bookings"),
        api.get("/admin/reviews"),
        api.get("/admin/reports"),
      ]);
      setStats(s.data); setUsers(u.data); setEvents(e.data);
      setBookings(b.data); setReviews(r.data); setReports(rp.data);
    } catch (e) {
      toast.error("Erreur de chargement");
    }
  };
  useEffect(() => { load(); }, []);

  const refundBooking = async (b) => {
    if (!confirm(`Rembourser intégralement ${b.amount?.toFixed(2)}€ à l'acheteur ?`)) return;
    try {
      await api.post(`/admin/bookings/${b.id}/refund`, {});
      toast.success("Remboursement initié");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors du remboursement");
    }
  };

  const removeUser = async (id) => {
    if (!confirm("Supprimer cet utilisateur ?")) return;
    await api.delete(`/admin/user/${id}`);
    toast.success("Supprimé"); load();
  };

  const toggleSuspend = async (u) => {
    const next = !u.suspended;
    if (!confirm(next ? `Suspendre ${u.email} ?` : `Réactiver ${u.email} ?`)) return;
    try {
      await api.patch(`/admin/user/${u.user_id}/suspend`, { suspended: next });
      toast.success(next ? "Utilisateur suspendu" : "Utilisateur réactivé");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const toggleEditorial = async (eventId, current) => {
    await api.patch(`/admin/events/${eventId}/editorial-pick`, { editorial_pick: !current });
    toast.success(current ? "Sélection retirée" : "Sélection éditoriale ajoutée");
    load();
  };

  const deleteReview = async (rid) => {
    if (!confirm("Supprimer cet avis ?")) return;
    await api.delete(`/admin/reviews/${rid}`);
    toast.success("Avis supprimé"); load();
  };

  const updateReport = async (id, status) => {
    await api.patch(`/admin/reports/${id}`, { status });
    toast.success("Statut mis à jour"); load();
  };

  const tabs = [
    { id: "overview", label: "Vue d'ensemble" },
    { id: "users", label: "Utilisateurs" },
    { id: "events", label: "Événements" },
    { id: "bookings", label: "Réservations" },
    { id: "reviews", label: `Avis (${reviews.length})` },
    { id: "reports", label: `Signalements (${reports.filter(r => r.status === "open").length})` },
  ];

  return (
    <DashboardShell title="Administration" subtitle="Vue globale de la plateforme">
      {stats.stripe_mode && (
        <div
          data-testid="stripe-mode-banner"
          className={`mb-6 px-4 py-3 rounded-md text-sm font-semibold flex items-center gap-2 ${
            stats.stripe_mode === "live"
              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
              : "bg-amber-50 text-amber-800 border border-amber-200"
          }`}
        >
          {stats.stripe_mode === "live"
            ? "🟢 Paiements Stripe : MODE LIVE — les paiements sont réels"
            : "🟠 Paiements Stripe : MODE TEST — aucun argent réel ne circule (carte test 4242…)"}
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-10">
        <StatCard label="Utilisateurs" value={stats.users || 0} />
        <StatCard label="Événements" value={stats.events || 0} />
        <StatCard label="Lieux" value={stats.venues || 0} />
        <StatCard label="Bookings" value={stats.bookings || 0} />
        <StatCard label="Volume" value={`${(stats.gross_volume || 0).toFixed(0)}€`} />
        <StatCard label="Commissions" value={`${(stats.platform_commission || 0).toFixed(0)}€`} accent />
      </div>

      <div className="flex gap-1 mb-6 border-b border-ink/10 overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.id}
            data-testid={`admin-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-semibold whitespace-nowrap ${tab === t.id ? "border-b-2 border-accent text-ink" : "text-muted-foreground hover:text-ink"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <>
        <div className="mb-6">
          <RevenueChart
            testId="admin-revenue-chart"
            title="Évolution du volume et des commissions (12 derniers mois)"
            data={stats.monthly || []}
            series={[
              { key: "gross", name: "Volume encaissé", color: "#18181A" },
              { key: "commission", name: "Revenu plateforme", color: "#A07E45" },
            ]}
          />
        </div>
        <div className="bg-white border border-ink/10 rounded-md p-8">
          <h3 className="font-heading font-medium text-lg text-ink mb-4">Activité récente</h3>
          <p className="text-muted-foreground text-sm">
            La plateforme génère <span className="font-bold text-ink">{(stats.platform_commission || 0).toFixed(2)}€</span> de commissions sur un volume total de {(stats.gross_volume || 0).toFixed(2)}€.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6 pt-6 border-t border-ink/10">
            <div>
              <p className="text-label text-muted-foreground mb-1">Avis publiés</p>
              <p className="font-heading text-2xl text-ink">{reviews.length}</p>
            </div>
            <div>
              <p className="text-label text-muted-foreground mb-1">Signalements ouverts</p>
              <p className="font-heading text-2xl text-accent">{reports.filter(r => r.status === "open").length}</p>
            </div>
            <div>
              <p className="text-label text-muted-foreground mb-1">Utilisateurs suspendus</p>
              <p className="font-heading text-2xl text-destructive">{users.filter(u => u.suspended).length}</p>
            </div>
          </div>
        </div>
        </>
      )}

      {tab === "users" && (
        <div className="bg-white border border-ink/10 rounded-md">
          <div className="divide-y divide-ink/10">
            {users.map((u) => (
              <div key={u.user_id} className="p-4 flex justify-between items-center gap-3" data-testid={`admin-user-${u.user_id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold truncate">{u.name}</p>
                    {u.suspended && <Badge variant="destructive" className="text-[10px]">SUSPENDU</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Badge variant="outline" className="capitalize">{u.role}</Badge>
                  {u.role !== "admin" && (
                    <>
                      <button
                        data-testid={`admin-user-suspend-${u.user_id}`}
                        onClick={() => toggleSuspend(u)}
                        title={u.suspended ? "Réactiver" : "Suspendre"}
                        className="p-2 hover:bg-cream-100 rounded-md"
                      >
                        {u.suspended
                          ? <ShieldCheck className="h-4 w-4 text-trust" />
                          : <ShieldOff className="h-4 w-4 text-accent" />}
                      </button>
                      <Button variant="ghost" size="sm" onClick={() => removeUser(u.user_id)} data-testid={`admin-user-delete-${u.user_id}`}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "events" && (
        <div className="bg-white border border-ink/10 rounded-md">
          <div className="divide-y divide-ink/10">
            {events.map((e) => (
              <div key={e.id} className="p-4 flex justify-between items-center gap-3">
                <div className="min-w-0">
                  <p className="font-medium truncate">{e.title}</p>
                  <p className="text-xs text-muted-foreground">{e.city} · {e.category}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    data-testid={`editorial-pick-${e.id}`}
                    onClick={() => toggleEditorial(e.id, e.editorial_pick)}
                    title="Sélection éditoriale"
                    className={`inline-flex items-center gap-1 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] font-bold transition-colors ${
                      e.editorial_pick
                        ? "bg-accent text-white"
                        : "bg-cream-50 text-ink/55 hover:bg-ink/5 border border-ink/10"
                    }`}
                  >
                    <BookmarkCheck className="h-3 w-3" />
                    {e.editorial_pick ? "Sélection" : "Sélectionner"}
                  </button>
                  <Badge className={e.status === "approved" ? "bg-trust/10 text-trust border border-trust/20" : "bg-cream-200 text-accent"}>{e.status}</Badge>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "bookings" && (
        <div className="bg-white border border-ink/10 rounded-md">
          <div className="p-4 border-b border-ink/10 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-label text-muted-foreground">Toutes les transactions</p>
            <div className="flex items-center gap-2">
              <select
                data-testid="admin-export-period-select"
                value={exportPeriod}
                onChange={(e) => setExportPeriod(e.target.value)}
                className="border border-ink/15 bg-white text-sm px-2 py-1.5 focus:outline-none focus:border-ink"
              >
                {PERIOD_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <Button
                data-testid="admin-export-csv-btn"
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    const res = await api.get(`/admin/transactions/export.csv${exportQuery(exportPeriod)}`, { responseType: "blob" });
                    const url = URL.createObjectURL(res.data);
                    const a = document.createElement("a");
                    a.href = url;
                    const [s, e] = periodRange(exportPeriod);
                    a.download = s
                      ? `transactions-eventmarketpro-${s}-au-${e}.csv`
                      : `transactions-eventmarketpro-${new Date().toISOString().slice(0, 10)}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                    toast.success("Export CSV téléchargé");
                  } catch {
                    toast.error("Erreur lors de l'export");
                  }
                }}
              >
                <Download className="h-3.5 w-3.5 mr-1.5" /> Exporter CSV
              </Button>
            </div>
          </div>
          <div className="divide-y divide-ink/10">
            {bookings.map((b) => (
              <div key={b.id} className="p-4 flex justify-between items-center">
                <div>
                  <p className="font-semibold">{b.item_title}</p>
                  <p className="text-xs text-muted-foreground">{b.id}</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="font-bold text-ink">{b.amount?.toFixed(2)}€</p>
                    <p className="text-xs text-trust">Commission: {b.commission?.toFixed(2)}€</p>
                  </div>
                  {b.status === "refunded" ? (
                    <Badge className="bg-cream-200 text-accent" data-testid={`admin-refunded-${b.id}`}>Remboursé</Badge>
                  ) : b.payment_status === "paid" ? (
                    <Button size="sm" variant="outline" data-testid={`admin-refund-${b.id}`} onClick={() => refundBooking(b)}>
                      Rembourser
                    </Button>
                  ) : (
                    <Badge variant="outline" className="text-[10px] uppercase">{b.status}</Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "reviews" && (
        <div className="bg-white border border-ink/10 rounded-md">
          {reviews.length === 0 ? (
            <p className="p-8 text-center text-muted-foreground text-sm">Aucun avis publié pour l'instant.</p>
          ) : (
            <div className="divide-y divide-ink/10">
              {reviews.map((r) => (
                <div key={r.id} className="p-4" data-testid={`admin-review-${r.id}`}>
                  <div className="flex justify-between items-start gap-3 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-[10px] uppercase">{r.target_type}</Badge>
                        <p className="text-sm font-semibold truncate">{r.target_label}</p>
                      </div>
                      <p className="text-xs text-muted-foreground">par {r.user_name} · {new Date(r.created_at).toLocaleDateString("fr-FR")}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="flex items-center gap-1 text-accent">
                        {[1,2,3,4,5].map(i => (
                          <Star key={i} className={`h-3 w-3 ${i <= r.rating ? "fill-accent" : "text-ink/20"}`} />
                        ))}
                      </div>
                      <button
                        onClick={() => deleteReview(r.id)}
                        data-testid={`admin-review-delete-${r.id}`}
                        className="p-1.5 hover:bg-destructive/10 rounded-md"
                        title="Supprimer"
                      >
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </button>
                    </div>
                  </div>
                  {r.comment && <p className="text-sm text-ink/70 italic">"{r.comment}"</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "reports" && (
        <div className="bg-white border border-ink/10 rounded-md">
          {reports.length === 0 ? (
            <p className="p-8 text-center text-muted-foreground text-sm">Aucun signalement pour l'instant.</p>
          ) : (
            <div className="divide-y divide-ink/10">
              {reports.map((rp) => {
                const statusColor = {
                  open: "bg-accent/10 text-accent border-accent/20",
                  reviewing: "bg-trust/10 text-trust border-trust/20",
                  resolved: "bg-cream-200 text-ink/60 border-ink/10",
                  dismissed: "bg-cream-100 text-ink/40 border-ink/10",
                }[rp.status] || "bg-cream-100 text-ink/60";
                return (
                  <div key={rp.id} className="p-4" data-testid={`admin-report-${rp.id}`}>
                    <div className="flex justify-between items-start gap-3 mb-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Flag className="h-3 w-3 text-accent" />
                          <Badge variant="outline" className="text-[10px] uppercase">{rp.target_type}</Badge>
                          <span className="text-xs text-muted-foreground font-mono">{rp.target_id}</span>
                        </div>
                        <p className="text-sm text-ink/85 mb-2">"{rp.reason}"</p>
                        <p className="text-xs text-muted-foreground">par {rp.reporter_name} · {new Date(rp.created_at).toLocaleDateString("fr-FR")}</p>
                      </div>
                      <Badge className={`${statusColor} border text-[10px] uppercase`}>{rp.status}</Badge>
                    </div>
                    {rp.status === "open" && (
                      <div className="flex gap-2 mt-3">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateReport(rp.id, "reviewing")}
                          data-testid={`report-review-${rp.id}`}
                        >
                          Prendre en charge
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateReport(rp.id, "resolved")}
                          data-testid={`report-resolve-${rp.id}`}
                        >
                          Résoudre
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => updateReport(rp.id, "dismissed")}
                          data-testid={`report-dismiss-${rp.id}`}
                        >
                          Ignorer
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </DashboardShell>
  );
}
